import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  setQuizInfo,
  setSelectedSport
} from '../../redux/slices/quizSlice';
import { setActiveCategory, setCompletedCategories, resetQuizProgress } from '../../redux/slices/forcedChoiceQuizSlice';
import { addAssessment } from '../../redux/slices/athleteSlice';
import { TRAIT_CATEGORIES } from '../../utils/traitCategories';
import useFetchTraitAssessmentProgress from '../../hooks/quiz/useFetchTraitAssessmentProgress';
import useSubmitTraitAssessment from '../../hooks/quiz/useSubmitTraitAssessment';

import { getDaysUntilEligible } from '../../utils/traitCooldown';
import TraitCooldownModal from '../../components/TraitCooldownModal/TraitCooldownModal';
import WelcomeMessage from '../../components/WelcomeMessage/WelcomeMessage';
import QuizProgressBar from '../../components/QuizProgressBar/QuizProgressBar';
import ViewCoachRequestButton from '../../components/ViewCoachRequestButton/ViewCoachRequestButton';
import CoachAcceptModal from '../../components/CoachAcceptModal/CoachAcceptModal';
import StartQuizButton from '../../components/StartQuizButton/StartQuizButton';
import GameIQModal from '../../components/GameIQModal/GameIQModal';
// import StartETSQuizButton from '../../components/StartQuizButton/StartETSQuizButton';
import StartTraitQuizButton from '../../components/StartQuizButton/StartTraitQuizButton';
// import GScoreGraphArea from '../../components/GScoreGraphArea/GScoreGraphArea';
import QuizResultsArea from '../../components/QuizResultsArea/QuizResultsArea';
import LearningLibraryCard from '../../components/LearningLibraryCard/LearningLibraryCard';
import TraitProfileCard from '../../components/TraitProfileCard/TraitProfileCard';
import MyCoaches from '../../components/MyCoaches/MyCoaches';
import ToastPopup from '../../components/ToastPopup/ToastPopup';
import Dropdown from '../../components/Dropdown/Dropdown';
import Tooltip from '../../components/Tooltip/Tooltip';
import Copyright from '../../components/Copyright/Copyright';
import DigitalClock from '../../components/DigitalClock/DIgitalClock';

import useToast from '../../hooks/ui/useToast';
import useMyCoachRequests from '../../hooks/coachRequest/useMyCoachRequests';
import useRespondToCoachRequest from '../../hooks/coachRequest/useRespondToCoachRequest';

import styles from './AthleteDashboard.module.css';

const TOTAL_LEVELS = 5;

const AthleteDashboard = () => {
  useOutletContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isCooldownModalOpen, setIsCooldownModalOpen] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);

  const { profile, assessments, loading, error } = useSelector((state) => state.athlete);
  const selectedSport = useSelector(state => state.quiz.sport);

  const { coachRequests, refetch } = useMyCoachRequests();
  const { fetchAllTraitProgress } = useFetchTraitAssessmentProgress();
  const { submitAssessment } = useSubmitTraitAssessment();
  const { respond } = useRespondToCoachRequest();
  const { toast, showToast, closeToast } = useToast(3000);

  const [isCoachRequestModalOpen, setIsCoachRequestModalOpen] = useState(false);
  const [isGameIQModalOpen, setIsGameIQModalOpen] = useState(false);

  const sportOptions = useMemo(() => {
    if (!profile?.unlockedGameIQLevels) return [];
    return profile.unlockedGameIQLevels.map(entry => ({
      label: entry.sport.charAt(0).toUpperCase() + entry.sport.slice(1),
      value: entry.sport
    }));
  }, [profile]);

  const gameIQAttempts = useMemo(() => {
    return assessments?.filter(a => a.type === 'game-iq') || [];
  }, [assessments]);
  
  // const latestETSAttempt = useMemo(() => {
  //   if (!assessments) return null;
  //   const etsAttempts = assessments.filter(a => a.type === 'ets-personality');
  //   if (etsAttempts.length === 0) return null;
  //   etsAttempts.sort((a, b) => new Date(b.dateTaken) - new Date(a.dateTaken));
  //   return etsAttempts[0];
  // }, [assessments]);

  const latestTraitAssessment = useMemo(() => {
    if (!assessments) return null;
    return assessments
      .filter((a) => a.type === 'trait-assessment')
      .sort((a, b) => new Date(b.dateTaken) - new Date(a.dateTaken))[0] || null;
  }, [assessments]);

  const getLevelOptions = (sport) => {
    if (!profile?.unlockedGameIQLevels) return [];
    const entry = profile.unlockedGameIQLevels.find(e => e.sport === sport);
    return (entry?.levels || []).map(level => ({ label: `Level ${level}`, value: level }));
  };

  const handleGameIQBegin = (sport, level) => {
    dispatch(setQuizInfo({ sport, level, questions: [] }));
    navigate('/dashboard/quiz');
  };

  // const handleStartETSQuizClick = () => {
  //   navigate('/dashboard/gscore-quiz');
  // };

  const handleStartTraitQuizClick = async () => {

    if (latestTraitAssessment) {
      const days = getDaysUntilEligible(latestTraitAssessment.dateTaken);

      if (days > 0) {
        setDaysRemaining(days -1);
        setIsCooldownModalOpen(true);
        return;
      }
    }

    try {
      const { data } = await fetchAllTraitProgress();
      const completed = data?.getAllTraitAssessmentProgress
        ?.filter((p) => p.isCompleted)
        .map((p) => p.category) || [];
  
      dispatch(setCompletedCategories(completed));
  
      const nextCategory = TRAIT_CATEGORIES.find(
        (cat) => !completed.includes(cat)
      );
  
      if (nextCategory) {
        dispatch(resetQuizProgress());
        dispatch(setActiveCategory(nextCategory));
        navigate('/dashboard/trait-quiz');
      } else if (!latestTraitAssessment) {
        try {
          const result = await submitAssessment();
          const attempt = result?.data?.submitTraitAssessment;
          if (attempt) dispatch(addAssessment(attempt));
          showToast('Assessment submitted successfully!', 'success');
        } catch (err) {
          showToast('Submission failed. Please try again.', 'error');
        }
      }
    } catch (err) {
      console.error('Failed to load trait quiz progress:', err);
    }
  };

  const handleAccept = async (requestId) => {
    try {
      await respond(requestId, true);
      showToast(`Accepted coach successfully`, 'success');
      refetch();
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    }
  };

  const handleDecline = async (requestId) => {
    try {
      await respond(requestId, false);
      showToast(`Declined coach request`, 'success');
      refetch();
    } catch (err) {
      showToast(`Error: ${err.message}`, 'error');
    }
  };

  const handleSportSelect = (sport) => {
    dispatch(setSelectedSport(sport));
  };

  useEffect(() => {
    if (profile?.sports?.length > 0 && !selectedSport) {
      dispatch(setSelectedSport(profile.sports[0]));
    }
  }, [profile, selectedSport, dispatch]);

  const completedLevels = useMemo(() => {
    if (!assessments || !selectedSport) return [];
    const levels = assessments
      .filter(a => a.sport === selectedSport && a.passed)
      .map(a => a.level);
    return [...new Set(levels)]; 
  }, [assessments, selectedSport]);

  const progress = Math.round((completedLevels.length / TOTAL_LEVELS) * 100);

  if (loading) return <p className={styles.loading}>Loading your dashboard...</p>;
  if (error) return <p className={styles.error}>Error: {error}</p>;

  return (
    <div className={styles.dashboardWrapper}>
      <ToastPopup
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={closeToast}
      />

      <div className={styles.topRow}>
        <WelcomeMessage name={profile?.firstName || 'Athlete'} sport={selectedSport} />
        <QuizProgressBar progress={progress} /> 
        
        <div className={styles.startQuizWrapper}>
          <StartQuizButton onClick={() => setIsGameIQModalOpen(true)} />
        </div>
        {/* <StartETSQuizButton onClick={handleStartETSQuizClick} /> */}
        <StartTraitQuizButton onClick={handleStartTraitQuizClick} />
        <ViewCoachRequestButton onClick={() => setIsCoachRequestModalOpen(true)} pendingCount={coachRequests.length} />
      </div>

      <div style={{ marginTop: 'var(--space-sm)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Dropdown
          options={sportOptions}
          value={selectedSport}
          onChange={handleSportSelect}
          placeholder="Select Sport"
        />
        <Tooltip text="Filters the progress bar by sport">
          <span style={{ fontSize: '1rem', opacity: 0.5, cursor: 'default', color: 'var(--color-text)' }}>ⓘ</span>
        </Tooltip>
      </div>

      <div className={styles.secondRow}>
        <TraitProfileCard
          profile={latestTraitAssessment?.profile || null}
          confidence={latestTraitAssessment?.confidence || 0}
          blend={latestTraitAssessment?.blend || null}
          id={latestTraitAssessment?._id || null}
        />
        <QuizResultsArea attempts={gameIQAttempts} />
        <MyCoaches coaches={profile?.coaches} />
        {/* <GScoreGraphArea etsAttempt={latestETSAttempt} /> */}
        <LearningLibraryCard />
      </div>

      <div className={styles.thirdRow}>
        <Copyright />
        <DigitalClock />
      </div>

      <CoachAcceptModal
        isOpen={isCoachRequestModalOpen}
        onClose={() => setIsCoachRequestModalOpen(false)}
        coachRequests={coachRequests}
        loading={loading}
        onAccept={handleAccept}
        onDecline={handleDecline}
      />
      <TraitCooldownModal
        isOpen={isCooldownModalOpen}
        daysRemaining={daysRemaining}
        onClose={() => setIsCooldownModalOpen(false)}
      />
      <GameIQModal
        isOpen={isGameIQModalOpen}
        onClose={() => setIsGameIQModalOpen(false)}
        onBegin={handleGameIQBegin}
        sportOptions={sportOptions}
        getLevelOptions={getLevelOptions}
        initialSport={selectedSport}
      />
    </div>
  );
};

export default AthleteDashboard;